import { DateTime } from 'luxon';
import type { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { List, Map } from 'immutable';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import VirtualList from 'react-tiny-virtual-list-dmk';

import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionProps, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { numberUtils } from 'app-components/utils/number';

import Check from '@mui/icons-material/Check';
import Checkbox from '@mui/material/Checkbox';
// MUI
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import Dump from 'components/Dump';
import QButton from 'components/QButton';
// LOCAL
import AmountField, { ShowColorEnum } from 'components/QuickenControls/AmountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import ZeroStateView from 'components/ZeroStateView';
import { useAppSelector } from 'hooks/redux';

import type { RootState } from 'store';

const useStyles = makeStyles()((theme) => ({
  VL: {
    padding: '8px 8px',
    '&::-webkit-scrollbar': {
      WebkitAppearance: 'none',
      width: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 2,
      backgroundColor: theme.palette.background.paper,
      WebkitBoxShadow: '0 0 1px rgba(245, 245, 245, 0.5)',
    },
  },
  VLList: {
    padding: 0,
    '&::-webkit-scrollbar': {
      WebkitAppearance: 'none',
      width: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 2,
      backgroundColor: theme.palette.background.paper,
      WebkitBoxShadow: '0 0 1px rgba(245, 245, 245, 0.5)',
    },
  },
  txnLine: {
    height: 56,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 8,
    padding: 16,
    boxShadow: theme.shadows[2],
    '&:hover': {
      transform: 'translate(0px, 0px)',
      backgroundColor: theme.palette.background.default,
      cursor: 'pointer',
    },
  },
  txnLineShaded: {
    height: 56,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 8,
    padding: 16,
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.primary.light,
    '&:hover': {
      transform: 'translate(0px, 0px)',
      cursor: 'pointer',
    },
  },
  txnLineList: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 8px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
      cursor: 'pointer',
    },
  },
  txnLineListSelected: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 8px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.primary.light,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  dater: {
    // 30
    width: 136,
    paddingRight: 16,
  },
  payer: {
    // 31
    width: '31%',
    minWidth: 90,
    paddingRight: 16,
  },
  selectedText: {
    color: theme.palette.primary.main,
  },
  typer: {
    // 28
    width: '28%',
    paddingRight: 16,
    fontSize: theme.palette.register.fontSize.default,
  },
  typerSplit: {
    width: '28%',
    paddingRight: 16,
    fontSize: theme.palette.register.fontSize.default,
    lineHeight: '24px',
    cursor: 'pointer',
  },
  amter: {
    // 18
    width: '16%',
    textAlign: 'right',
    minWidth: 64,
  },
  checkSlot: {
    height: 24,
    width: 24,
    marginRight: 8,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkIcon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  stateView: {
    width: '100%',
  },
  zeroReset: {
    display: 'flex',
    textAlign: 'center',
    justifyContent: 'center',
    height: 32,
  },
  resetText: {
    color: theme.palette.text.secondary,
    lineHeight: '32px',
  },
  listVariantRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  listVariantContainer: {
    flex: 1,
    minHeight: 0,
  },
  zeroState: {
    height: 'auto',
  },
}));

// Selectors passed via `transactionListSelector` may return a keyed Map of Records (e.g.
// getTransactionsByTextFilter), a pre-sorted List of Records (e.g. getLesserBilledTransactions),
// or a pre-sorted List of plain transaction props (e.g. getSortedTransactionsForAccountIds).
type AccountTransactions =
  | ImmutableMap<string, TransactionRecord>
  | ImmutableList<TransactionRecord>
  | ImmutableList<TransactionProps>;

// Stable fallback so useAppSelector doesn't see a new reference (and re-render) on every store update.
const EMPTY_TXNS = Map<string, TransactionRecord>();

// Either an Immutable collection of ids (multiSelect) or a single selected id.
type HighlightValue = string | null | { contains?: (id?: string) => boolean };

interface TransactionSelectListProps {
  clickFunc?: (e: React.SyntheticEvent) => void;
  highlight?: HighlightValue;
  hideCategory?: boolean;
  variant?: 'card' | 'list';
  multiSelect?: boolean;
  onReset?: () => void;
  listMinHeight?: number;
  listMaxHeight?: number;
  listItemSize?: number;
  filterByType?: (txn: TransactionProps) => boolean;
  transactions?: ImmutableMap<string, TransactionRecord>;
  selectorProps?: Record<string, unknown>;
  filter?: string | null;
  transactionListSelector?: (state: RootState, props: Record<string, unknown>) => AccountTransactions;
}

function TransactionSelectList(props: TransactionSelectListProps) {
  const {
    clickFunc,
    filter,
    filterByType,
    hideCategory,
    highlight,
    listItemSize = 60,
    listMaxHeight = 572,
    listMinHeight = 360,
    multiSelect,
    onReset,
    selectorProps,
    transactionListSelector,
    transactions,
    variant = 'card',
  } = props;

  const acctTxns = useAppSelector((state) =>
    transactions
      ? transactionsSelectors.getTransactionsByTextFilter(state, { transactions, filter })
      : (transactionListSelector?.(state, { ...selectorProps, filter }) ?? EMPTY_TXNS),
  );

  const { classes } = useStyles();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  const isListVariant = variant === 'list';

  // A callback ref (rather than a mount-once useEffect keyed off a useRef) re-attaches the
  // observer whenever the DOM node changes, since the zero-state and list branches below render
  // different container elements (the list branch has a Divider in front), so React remounts this
  // div when results go from empty to non-empty.
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      if (!isListVariant || !node || typeof ResizeObserver === 'undefined') {
        return;
      }
      const resizeObserver = new ResizeObserver(([entry]) => {
        const height = entry?.contentRect.height;
        if (height) {
          setMeasuredHeight((prev) => (prev === height ? prev : height));
        }
      });
      resizeObserver.observe(node);
      resizeObserverRef.current = resizeObserver;
    },
    [isListVariant],
  );

  const checkHighlight = (id?: string): boolean => {
    if (multiSelect) {
      return (typeof highlight === 'object' && highlight?.contains?.(id)) ?? false;
    }
    return highlight === id;
  };

  const getCategoryField = (txn: TransactionProps, extraClassName?: string) =>
    !transactionsUtils.isSplitTxn(txn) ? (
      <CategoryField
        variant='paragraphRegular'
        classNameWhenNotEditable={extraClassName ? `${classes.typer} ${extraClassName}` : classes.typer}
        disableTooltip
        value={txn.coa}
        longCats={false}
        autoFocus={false}
        noWrap
      />
    ) : (
      <Typography className={extraClassName ? `${classes.typerSplit} ${extraClassName}` : classes.typerSplit}>
        --- Split ---
      </Typography>
    );

  // Accepts pre-filtered acctTxns to avoid filtering twice per render.
  const generateTxns = (filteredTxns: AccountTransactions) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        clickFunc?.(e);
      }
    };
    return filteredTxns
      .valueSeq()
      .map((txn, i) => {
        const selected = checkHighlight(txn.id ?? undefined);
        const isSelectedListRow = isListVariant && selected;
        const withSelected = (base: string) => (isSelectedListRow ? `${base} ${classes.selectedText}` : base);
        return (
          <div
            role='button'
            key={txn.id ?? i}
            data-name={`txn-item-${i}`}
            tabIndex={0}
            id={JSON.stringify(txn)}
            onClick={clickFunc}
            onKeyDown={handleKeyDown}
            className={
              isListVariant
                ? selected
                  ? classes.txnLineListSelected
                  : classes.txnLineList
                : selected
                  ? classes.txnLineShaded
                  : classes.txnLine
            }
          >
            <Dump obj={txn} />
            {isListVariant && (
              <span className={classes.checkSlot}>{selected ? <Check className={classes.checkIcon} /> : null}</span>
            )}
            {multiSelect && (
              <Checkbox
                checked={selected}
                // no onChange because we want the parent div to fire the event to retain the txn in the id
              />
            )}
            <Typography variant='paragraphRegular' noWrap className={withSelected(classes.dater)}>
              {DateTime.fromISO(txn.postedOn).toFormat('DD')}
            </Typography>
            <Typography
              variant={isListVariant ? 'paragraphMedium' : 'paragraphRegular'}
              noWrap
              className={withSelected(classes.payer)}
            >
              {txn.payee}
            </Typography>
            {!hideCategory && getCategoryField(txn, isSelectedListRow ? classes.selectedText : undefined)}
            <AmountField
              showSign={numberUtils.ShowSignEnum.POSITIVE_ONLY}
              showColor={isSelectedListRow ? ShowColorEnum.MANUAL : ShowColorEnum.POSITIVE_ONLY}
              color={isSelectedListRow ? 'primary' : txn.amount > 0 ? 'green' : undefined}
              value={txn.amount}
              variant='paragraphRegular'
              className={classes.amter}
              noWrap
            />
          </div>
        );
      })
      .toArray();
  };

  const acctTxnsFiltered: AccountTransactions = useMemo(() => {
    if (!filterByType) {
      return acctTxns;
    }
    // Both branches call the same `.filter(filterByType)` — the `List.isList` check exists only to
    // narrow the `List | Map` union so TS can resolve `.filter`'s overload; without it, filtering the
    // union directly doesn't type-check even though the two collections' `.filter` behave identically.
    return List.isList(acctTxns) ? acctTxns.filter(filterByType) : acctTxns.filter(filterByType);
  }, [acctTxns, filterByType]);
  const displayNodes = useMemo(
    () => generateTxns(acctTxnsFiltered),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acctTxnsFiltered, clickFunc, highlight, hideCategory, isListVariant, multiSelect],
  );
  // The 'list' variant fills whatever vertical space its container measures out to
  // (see the ResizeObserver effect above) instead of this heuristic clamp.
  const listHeight = isListVariant
    ? (measuredHeight ?? listMinHeight)
    : Math.min(Math.max(acctTxnsFiltered.size * listItemSize, listMinHeight), listMaxHeight);

  if (!acctTxnsFiltered?.size) {
    const zeroState = (
      <div className={classes.stateView} style={{ height: listHeight }}>
        <ZeroStateView
          className={classes.zeroState}
          size='small'
          qIllustration='accounts'
          title="We couldn't find any transactions"
          description='You might have searched for something too specific. Try resetting the search if so.'
        />
        <div className={classes.zeroReset}>
          <QButton onClick={onReset} variant='text'>
            Click Here
          </QButton>
          <Typography className={classes.resetText}>to reset the search.</Typography>
        </div>
      </div>
    );
    return isListVariant ? (
      <div className={classes.listVariantRoot}>
        <div ref={containerRef} className={classes.listVariantContainer}>
          {zeroState}
        </div>
      </div>
    ) : (
      zeroState
    );
  }
  const list = acctTxnsFiltered.size && (
    <VirtualList
      // @ts-expect-error react-tiny-virtual-list-dmk's Props type omits `id`, but it forwards
      // unknown props onto the rendered div; the Cypress page objects query this list by id.
      id={'transaction-choice-list'}
      height={listHeight}
      itemCount={displayNodes.length}
      overscanCount={30}
      itemSize={listItemSize}
      renderItem={({ style, index }) =>
        displayNodes[index] ? (
          <div style={style} key={index}>
            {displayNodes[index]}
          </div>
        ) : (
          ''
        )
      }
      className={isListVariant ? classes.VLList : classes.VL}
    />
  );
  return isListVariant ? (
    <div className={classes.listVariantRoot}>
      <Divider />
      <div ref={containerRef} className={classes.listVariantContainer}>
        {list}
      </div>
    </div>
  ) : (
    list
  );
}

export default React.memo(TransactionSelectList);
